/** @jsx jsx */
import { useRef, useEffect, useCallback } from 'react';
import { jsx } from '@compiled/react';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import { token } from '@atlaskit/tokens';
import { useAcronymHighlighter } from '@atlassian/jira-highlight-actions/src/controllers/use-acronym-highlighter/index.tsx';
import { useOnHighlight } from '@atlassian/jira-highlight-actions/src/ui/utils.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsIssueOfIncidentsPractice } from '@atlassian/jira-issue-field-servicedesk-practices/src/services/use-practices-field-value/index.tsx';
import { useOpenGICFromComment } from '@atlassian/jira-create-issue-from-comment/src/controllers/use-open-gic-from-comment/index.tsx';
import { useIsTargetedForCreateIssueFromCommentExp } from '@atlassian/jira-create-issue-from-comment/src/controllers/is-targeted-for-create-issue-from-comment-exp/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	useIsBusinessIssue,
	useIsSoftwareIssue,
} from '@atlassian/jira-issue-util-hooks/src/index.tsx';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import { COMMENT_SLACK_FLAG_DISMISSED } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { useFlagsService } from '@atlassian/jira-flags/src/services/index.tsx';
import { CORE_PROJECT, SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { CommentBase as CommentBaseLegacy } from './legacy-view.tsx';
import { FIELD_TYPE_COMMENT } from './constants.tsx';
import type { ComponentProps as Props } from './types.tsx';
import { CommentBase as CommentBaseNew } from './view.tsx';
import messages from './messages.tsx';

const CommentBase = componentWithCondition(
	() => {
		const isSoftwareProject = useIsSoftwareIssue();
		const isBusinessProject = useIsBusinessIssue();
		if (!fg('jira_custom_comment_component_refactor')) {
			return false;
		}
		if (!isSoftwareProject && !isBusinessProject) {
			return false;
		}
		return true;
	},
	CommentBaseNew,
	CommentBaseLegacy,
);
const COMMENT_SLACK_FLAG_ID = 'comment_slack_id';

const CommentWithIssueOfIncidentsPracticeAndAiDefinitions = (props: Props) => {
	const { author } = props;
	const isIssueOfIncidentsPractice = useIsIssueOfIncidentsPractice();
	const commentRef = useRef(null);
	const { highlightAcronyms, resetAcronymsData } = useAcronymHighlighter(
		commentRef,
		FIELD_TYPE_COMMENT,
		author?.id ?? undefined,
	);
	const { register, unregister } = useOnHighlight(commentRef, FIELD_TYPE_COMMENT);

	useEffect(() => {
		if (fg('thor_ai_definitions_issue_comment')) {
			if (commentRef.current && !props.isEditing) {
				highlightAcronyms(props.bodyAdf);
				return resetAcronymsData;
			}
		}
	}, [props.bodyAdf, highlightAcronyms, resetAcronymsData, props.isEditing]);

	useEffect(() => {
		if (fg('thor_ai_definitions_issue_comment')) {
			if (!props.isEditing) {
				register();
			}
			return unregister;
		}
	}, [unregister, register, props.isEditing]);

	return (
		<CommentBase
			{...props}
			commentRef={
				!props.isEditing && fg('thor_ai_definitions_issue_comment') ? commentRef : undefined
			}
			isIssueOfIncidentsPractice={isIssueOfIncidentsPractice}
		/>
	);
};

const CommentsWithCreateIssueFromComment = (props: Props) => {
	const { author, fullIssueUrl, bodyAdf } = props;
	const { onClickCreateIssue, currentUserAccountId } = useOpenGICFromComment({
		author,
		fullIssueUrl,
		bodyAdf,
	});

	return (
		<CommentWithIssueOfIncidentsPracticeAndAiDefinitions
			{...props}
			onClickCreateIssue={onClickCreateIssue}
			currentUserAccountId={currentUserAccountId}
		/>
	);
};

const CommentWithCreateIssueFromCommentFeatureFlags = (props: Props) => {
	const isTargetedForCreateIssueFromCommentExp = useIsTargetedForCreateIssueFromCommentExp();
	const commonProps = { ...props, hasFloatingMenu: false };

	if (
		isTargetedForCreateIssueFromCommentExp &&
		expVal('work_item_create_from_comment_and_pin_comment', 'hasWorkItemCreate', false)
	) {
		return <CommentsWithCreateIssueFromComment {...commonProps} />;
	}

	return <CommentWithIssueOfIncidentsPracticeAndAiDefinitions {...commonProps} />;
};

const CommentWithSlackInfo = (props: Props) => {
	const [commentSlackFlagDismissed, { setValue }] = useUserPreferencesValue(
		COMMENT_SLACK_FLAG_DISMISSED,
	);

	const { showFlag, dismissFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const onClickUnderstood = useCallback(() => {
		setValue('true');
		dismissFlag(COMMENT_SLACK_FLAG_ID);
		// TODO: fire analytics
	}, [dismissFlag, setValue]);

	const onShowFlagForSlackSync = useCallback(() => {
		showFlag({
			id: COMMENT_SLACK_FLAG_ID,
			icon: (
				<InfoIcon
					label={formatMessage(messages.slackFlagTitle)}
					color={token('color.icon.information')}
				/>
			),
			messageId: 'issue-comment-base.ui.comment.show-flag.info',
			messageType: 'transactional',
			type: 'info',
			title: formatMessage(messages.slackFlagTitle),
			description: formatMessage(messages.slackFlagBody),
			isAutoDismiss: false,
			actions: [
				{
					onClick: onClickUnderstood,
					content: formatMessage(messages.slackFlagBtn),
				},
			],
		});
	}, [onClickUnderstood, showFlag, formatMessage]);

	return (
		<CommentWithCreateIssueFromCommentFeatureFlags
			{...props}
			showCommentSlackFlag={props.isThirdPartySyncOn && commentSlackFlagDismissed !== 'true'}
			onShowFlagForSlackSync={onShowFlagForSlackSync}
		/>
	);
};

const CommentWithSlackInfoFeatureFlags = (props: Props) => {
	if (
		(props.projectType === SOFTWARE_PROJECT || props.projectType === CORE_PROJECT) &&
		!!props.thirdPartyCommentSource &&
		expVal('jira_slack_sync_slack_thread', 'isEnabled', false)
	) {
		return <CommentWithSlackInfo {...props} />;
	}
	return <CommentWithCreateIssueFromCommentFeatureFlags {...props} />;
};

export const Comment = CommentWithSlackInfoFeatureFlags;
export default Comment;
